import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Tile. Selectable surface card (settings hub / report types).
 * Mirrors .tile / .tile.is-sel in the showcase app.css: surface container, card
 * shadow, hover lift. Selection emphasis comes from a 3px primary-bg ring plus a
 * primary border — NOT a colored accent bar. 48px icon chip, bold 16 title,
 * 12 secondary description.
 */
export interface TileProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onSelect'> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  selected?: boolean;
  onSelect?: () => void;
}

export const Tile = React.forwardRef<HTMLDivElement, TileProps>(
  ({ className, icon, title, description, selected = false, onSelect, ...props }, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onSelect) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    };
    return (
      <div
        ref={ref}
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        aria-pressed={onSelect ? selected : undefined}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex flex-col gap-3 p-5 rounded-so-lg border border-sober-outline bg-sober-surface',
          'shadow-so-card cursor-pointer transition hover:-translate-y-0.5 hover:shadow-so-raised',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sober-primary-fg',
          selected && 'ring-[3px] ring-sober-primary-fg border-sober-primary',
          className
        )}
        {...props}
      >
        {icon ? (
          <span
            aria-hidden="true"
            className="grid h-12 w-12 place-items-center rounded-so-md bg-sober-primary-bg text-sober-primary text-so-20 [&_svg]:size-6"
          >
            {icon}
          </span>
        ) : null}
        <span className="text-so-16 font-bold text-sober-text">{title}</span>
        {description ? (
          <span className="text-so-12 text-sober-secondary">{description}</span>
        ) : null}
      </div>
    );
  }
);
Tile.displayName = 'Tile';
